import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Plus, Shapes, Swords, Users } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { api } from "../../api/client";
import type { App6Entry, EniOdbKind, OdbDetail, OdbKind, OdbNode, OdbOrganisation, OdbSide, OdbVocabulary } from "../../api/types";
import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { ChoiceCard } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Button, codeInputClass, Field, inputClass, Segmented } from "../../components/ui";
import { k, t, tc, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { onRadioGroupKeyDown, rovingTabIndex } from "../radioGroupKeys";
import { App6Palette } from "./App6Palette";
import { functionLabel, paletteOf } from "./app6";
import { eniRoleLabel } from "./eni";
import { nations } from "./nations";
import { cleanNumber, drawableNumber, leadingDigits, validNumber } from "./numbering";
import type { OrbatsCreate } from "./orbatsTarget";
import { shortApp6, sidcOf } from "./sidc";
import { amiStarterTree, defaultTf, eniStarterTree, rootEchelons, type TreeContext } from "./tree";
import { UnitSymbol } from "./UnitSymbol";
import { Waiting } from "../../components/Waiting";

/** « Nouvel ordre de bataille » : amis (nature, nom, numéros bas gauche et bas droite, fonction APP-6, organisation,
 *  nation, départ : structure type, racine seule ou copie d'un ordre de bataille fourni) ou ennemis (niveau de
 *  l'adversaire, nom, numéros, fonction APP-6 hostile, départ : structure ternaire générique ou racine seule). Un
 *  ordre de bataille ennemi créé ici n'a pas d'ennemi de référence : générique, il laisse au dossier l'ennemi choisi
 *  dans ses Données initiales. « Créer » bloqué désigne ce qui manque. */

const SIDES: { value: OdbSide; label: string; icon: typeof Users }[] = [
  { value: "AMI", label: k("Amis"), icon: Users },
  { value: "ENI", label: k("Ennemis"), icon: Swords },
];

const AMI_KINDS: { key: OdbKind; title: string; detail: string }[] = [
  { key: "gtia", title: k("Bataillon ou GTIA"), detail: k("Niveau régiment : ses compagnies (S/GTIA) tiennent les emplacements de la manœuvre.") },
  { key: "brigade", title: k("Brigade"), detail: k("Niveau brigade : ses bataillons (GTIA) tiennent les emplacements ; chacun sert aussi au niveau régiment.") },
  { key: "division", title: k("Division"), detail: k("Niveau division : ses brigades tiennent les emplacements, et servent aux niveaux inférieurs.") },
  { key: "corps", title: k("Corps d'armée"), detail: k("Niveau corps d'armée : ses divisions tiennent les emplacements, deux en premier échelon et une en second.") },
];

/** Composition de la structure ternaire générique de chaque nature ennemie (`ENI_TERNARY`, tree.ts). */
const TERNARY_DETAIL: Record<EniOdbKind, string> = {
  eni_bat: k("Trois compagnies de trois sections (deux en premier échelon, une en second), une compagnie de reconnaissance de trois sections, une compagnie d'appui et une section de drones."),
  eni_brig: k("Trois bataillons de trois compagnies (deux en premier échelon, un en second), un bataillon de reconnaissance, un bataillon d'artillerie et une compagnie de drones."),
  eni_div: k("Trois brigades de trois bataillons (deux en premier échelon, une en second), un bataillon de reconnaissance, une brigade d'artillerie et un bataillon de drones."),
  eni_corps: k("Trois divisions de trois brigades (deux en premier échelon, une en second), une brigade d'artillerie de corps et un bataillon de drones."),
};

/** Fonction proposée pour la racine : infanterie (bataillon ami), infanterie mécanisée (bataillon ennemi), sinon
 *  interarmes. */
const DEFAULT_FUNCTION: Record<OdbKind, string> = {
  gtia: "inf",
  brigade: "interarmes",
  division: "interarmes",
  corps: "interarmes",
  eni_bat: "inf_meca",
  eni_brig: "interarmes",
  eni_div: "interarmes",
  eni_corps: "interarmes",
};

type Start = "structure" | "racine" | "copie";

export function NewOdbDialog({
  vocabulary,
  initialSide,
  initial,
  referenceEnemy = true,
  onClose,
  onCreated,
}: {
  vocabulary: OdbVocabulary;
  initialSide: OdbSide;
  /** Création demandée depuis les Données initiales : camp, nature, départ. */
  initial: OrbatsCreate | null;
  /** Faux : demandé depuis un dossier qui affronte l'ennemi générique (registre B39) — aucun ennemi de référence
   *  évoqué. */
  referenceEnemy?: boolean;
  onClose: () => void;
  onCreated: (odb: OdbDetail) => void;
}) {
  const notify = useUi((s) => s.notify);
  const [side, setSide] = useState<OdbSide>(initial?.side ?? initialSide);
  const enemy = side === "ENI";
  const palette = paletteOf(vocabulary);
  const eniVocabulary = useQuery({ queryKey: ["odb-vocabulary", "ENI"], queryFn: () => api.odbVocabulary("ENI"), staleTime: Infinity, enabled: enemy });
  const amiListing = useQuery({ queryKey: ["odbs", "AMI"], queryFn: () => api.odbs("AMI"), enabled: !enemy });

  // --- Commun aux deux camps : nom, numéros, fonction APP-6, départ ---
  const [kind, setKind] = useState<OdbKind>(initial?.side === "AMI" ? initial.kind : "gtia");
  const [eniKind, setEniKind] = useState<EniOdbKind>(initial?.side === "ENI" ? (initial.kind as EniOdbKind) : "eni_bat");
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [numberTouched, setNumberTouched] = useState(false);
  const [higher, setHigher] = useState("");
  const [fonction, setFonction] = useState<{ key: string; app6: string } | null>(null);
  const [start, setStart] = useState<Start>(initial?.start ?? "structure");
  const [choosing, setChoosing] = useState(false);
  const shownKind: OdbKind = enemy ? eniKind : kind;
  const ctxVocabulary = enemy ? (eniVocabulary.data ?? vocabulary) : vocabulary;
  const ctx: TreeContext = useMemo(() => ({ kind: shownKind, palette, vocabulary: ctxVocabulary }), [shownKind, palette, ctxVocabulary]);
  const defaultEntry = (palette.entries.find((e) => e.key === DEFAULT_FUNCTION[shownKind]) ?? palette.entries[0]) as App6Entry;
  const chosenFunction = fonction ?? { key: defaultEntry.key, app6: defaultEntry.app6 };
  // Ennemi : pas de copie d'un ordre de bataille fourni (structure ternaire ou racine seule).
  const shownStart: Start = enemy && start === "copie" ? "structure" : start;

  // --- Amis : organisation, nation, copie d'un ordre de bataille fourni ---
  const [organisation, setOrganisation] = useState<OdbOrganisation>("gtia");
  const [nation, setNation] = useState("");
  const [copyCode, setCopyCode] = useState("");
  // Copie d'un ordre de bataille fourni : brigades et division du catalogue ; pour un bataillon (GTIA), les GTIA de
  // ses brigades, groupés par brigade (le catalogue n'a aucun GTIA seul). Aucune copie possible : départ grisé.
  const catalog = amiListing.data?.catalog ?? [];
  const copyChoices: { value: string; label: string; group: string | null }[] =
    kind === "gtia"
      ? catalog
          .filter((o) => o.kind === "brigade")
          .flatMap((o) => (o.gtias ?? []).map((g) => ({ value: `${o.code}/${g.code}`, label: g.name, group: o.name })))
      : catalog.filter((o) => o.kind === kind).map((o) => ({ value: o.code, label: o.name, group: null }));
  const copyGroups = [...new Set(copyChoices.map((c) => c.group))];
  const noCopy = amiListing.isSuccess && copyChoices.length === 0;

  const nameRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  const starter = (label: string): OdbNode => {
    const common = { name: label, number: cleanNumber(number), higher: cleanNumber(higher), fonction: chosenFunction.key, app6: chosenFunction.app6 };
    if (enemy) return eniStarterTree(ctx, { ...common, start: shownStart === "racine" ? "racine" : "structure" });
    return amiStarterTree(ctx, { ...common, organisation, nation: nation || null, start: shownStart === "racine" ? "racine" : "structure" });
  };

  const create = useMutation({
    mutationFn: async () => {
      const label = name.trim();
      if (!enemy && start === "copie") {
        // GTIA d'une brigade fournie : « brigade/GTIA ».
        const cut = kind === "gtia" ? copyCode.indexOf("/") : -1;
        return cut > 0 ? api.duplicateOdb(copyCode.slice(0, cut), copyCode.slice(cut + 1), label) : api.duplicateOdb(copyCode, null, label);
      }
      const tree = starter(label);
      // Ennemi parti de la structure ternaire : gardée comme origine (badge de l'éditeur et de la liste).
      return api.createOdb(shownKind, enemy && shownStart === "structure" ? { ...tree, model: { source: "structure", id: "ternaire" } } : tree);
    },
    onSuccess: onCreated,
  });

  // Ce qui manque, dans l'ordre où l'utilisateur le remplit ; la désignation vise le champ attendu.
  const missing: { ref: HTMLElement | null; text: string; kind?: "field" | "ring" } | null = (() => {
    if (enemy && !eniVocabulary.data) return null;
    if (!name.trim()) return { ref: nameRef.current, text: enemy ? t("Donnez un nom à l'ordre de bataille ennemi.") : t("Donnez un nom à la formation."), kind: "field" };
    if (!validNumber(number) || !validNumber(higher)) return { ref: numberRef.current, text: t("Numéros : chiffres seulement, quatre au plus."), kind: "field" };
    if (!enemy && start === "copie" && noCopy) return { ref: copyRef.current, text: t("Aucun ordre de bataille fourni de cette nature : choisissez un autre départ."), kind: "ring" };
    if (!enemy && start === "copie" && !copyCode) return { ref: copyRef.current, text: t("Choisissez l'ordre de bataille à copier."), kind: "ring" };
    return null;
  })();
  const showMissing = () => {
    if (!missing) return;
    designate(missing.ref, missing.kind);
    notify({ text: missing.text, tone: "info" });
  };
  const ready = !enemy || Boolean(eniVocabulary.data);

  const rootEchelon = rootEchelons(ctx)[0] ?? "16";
  const rootTf = defaultTf(ctx, organisation, rootEchelon);
  /** Symbole de la racine d'une nature, avec la fonction choisie (cartes « Nature » et « Niveau de l'adversaire »). */
  const kindSidc = (k: OdbKind) => {
    const c = { kind: k, palette, vocabulary: ctxVocabulary };
    const e = rootEchelons(c)[0] ?? "16";
    return sidcOf({ side, app6: chosenFunction.app6, echelon: e, tf: defaultTf(c, organisation, e) });
  };
  // Aperçu recalculé à chaque rendu : une vingtaine d'unités au plus, rien à mémoriser.
  const preview = !ready || (!enemy && start === "copie") ? null : starter(name.trim() || t("Sans nom"));

  const chooseSide = (next: OdbSide) => {
    setSide(next);
    setFonction(null);
  };

  /** Nom, numéros, fonction APP-6 : mêmes champs pour les deux camps. */
  const identity = (
    <>
      <Field label={enemy ? t("Nom de l'ordre de bataille ennemi") : t("Nom de la formation")}>
        <input
          ref={nameRef}
          autoFocus
          value={name}
          maxLength={80}
          placeholder={
            enemy
              ? eniKind === "eni_corps"
                ? t("Ex. Corps d'armée ennemi")
                : eniKind === "eni_div"
                  ? t("Ex. Division blindée ennemie")
                  : eniKind === "eni_brig"
                    ? t("Ex. Brigade mécanisée ennemie")
                    : t("Ex. Bataillon de reconnaissance ennemi")
              : kind === "gtia"
                ? t("Ex. 2 R ANGLIAN")
                : kind === "brigade"
                  ? t("Ex. 12 Bde")
                  : kind === "corps"
                    ? t("Ex. ARRC")
                    : t("Ex. 3 Div")
          }
          onChange={(e) => {
            setName(e.target.value);
            if (!numberTouched) setNumber(leadingDigits(e.target.value) ?? "");
          }}
          className={inputClass}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("Numéro (bas gauche)")} hint={!validNumber(number) ? t("Chiffres seulement, quatre au plus.") : t("Repris des chiffres du nom.")}>
          <input
            ref={numberRef}
            className={codeInputClass}
            inputMode="numeric"
            maxLength={8}
            value={number}
            aria-invalid={!validNumber(number) || undefined}
            onChange={(e) => {
              setNumber(e.target.value);
              setNumberTouched(true);
            }}
          />
        </Field>
        <Field label={t("Formation supérieure (bas droite)")} hint={!validNumber(higher) ? t("Chiffres seulement, quatre au plus.") : t("Facultative.")}>
          <input
            className={codeInputClass}
            inputMode="numeric"
            maxLength={8}
            value={higher}
            aria-invalid={!validNumber(higher) || undefined}
            onChange={(e) => setHigher(e.target.value)}
          />
        </Field>
      </div>
      <div>
        <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Fonction APP-6")}</span>
        <button
          type="button"
          onClick={() => setChoosing(true)}
          className="flex min-h-11 w-full items-center gap-2.5 rounded-lg border border-line-strong bg-surface px-2.5 py-1.5 text-left transition-colors hover:bg-hover"
        >
          <UnitSymbol sidc={sidcOf({ side, app6: chosenFunction.app6, echelon: rootEchelon })} size={12} box={36} />
          <span className="min-w-0 flex-1">
            <span className="block text-ui text-ink">{functionLabel(palette, chosenFunction.key, chosenFunction.app6)}</span>
            <span className="block font-mono text-caps text-ink-muted tabular-nums">{shortApp6(chosenFunction.app6)}</span>
          </span>
          <Shapes size={14} aria-hidden className="shrink-0 text-ink-muted" />
        </button>
      </div>
    </>
  );

  return (
    <>
      <Dialog
        title={enemy ? t("Nouvel ordre de bataille ennemi") : t("Nouvel ordre de bataille")}
        subtitle={
          enemy
            ? referenceEnemy
              ? t("Il remplace l'ennemi de référence des dossiers qui le choisissent dans leurs Données initiales.")
              : t("Il détaille l'ennemi générique des dossiers qui le choisissent dans leurs Données initiales.")
            : t("Symboles OTAN APP-6 et numéros : il devient une force des Données initiales, comme celles fournies avec Generex.")
        }
        onClose={onClose}
        width={640}
        dirty={Boolean(name.trim())}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !ready} blocked={missing !== null} onBlocked={showMissing} highlight={missing === null && ready}>
              {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {t("Créer")}
            </Button>
          </>
        }
      >
        <DialogBody className="space-y-4">
          <Segmented
            label={t("Amis ou ennemis")}
            options={SIDES.map((o) => ({ value: o.value, label: t(o.label), icon: o.icon }))}
            value={side}
            onChange={chooseSide}
          />
          {enemy ? (
            !eniVocabulary.data ? (
              eniVocabulary.isError ? (
                <Callout tone="danger">{eniVocabulary.error.message}</Callout>
              ) : (
                <p className="flex items-center gap-2 text-ui text-ink-muted">
                  <Spinner decorative /> {<Waiting>{t("Chargement…")}</Waiting>}
                </p>
              )
            ) : (
              <Motion origin="fade" className="space-y-4">
                <div
                  className="grid gap-2"
                  role="radiogroup"
                  aria-label={t("Niveau de l'adversaire")}
                  onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => setEniKind((eniVocabulary.data?.kinds[i]?.key ?? "eni_bat") as EniOdbKind))}
                >
                  <span className="block text-ui font-medium text-ink-soft">{t("Niveau de l'adversaire")}</span>
                  {eniVocabulary.data.kinds.map((option, i) => (
                    <ChoiceCard
                      key={option.key}
                      radio
                      selected={eniKind === option.key}
                      tabIndex={rovingTabIndex(i, eniVocabulary.data.kinds.findIndex((o) => o.key === eniKind), eniVocabulary.data.kinds.map(() => true))}
                      onClick={() => setEniKind(option.key as EniOdbKind)}
                      icon={<UnitSymbol sidc={kindSidc(option.key)} size={10} box={30} />}
                      title={tc("nature", option.label)}
                      detail={tServer(option.uses)}
                    />
                  ))}
                </div>
                {identity}
                <div className="grid gap-2" role="radiogroup" aria-label={t("Départ")}>
                  <span className="block text-ui font-medium text-ink-soft">{t("Départ")}</span>
                  <ChoiceCard
                    radio
                    selected={shownStart === "structure"}
                    tabIndex={shownStart === "structure" ? 0 : -1}
                    onClick={() => setStart("structure")}
                    title={t("Structure ternaire (générique)")}
                    detail={t(TERNARY_DETAIL[eniKind])}
                  />
                  <ChoiceCard
                    radio
                    selected={shownStart === "racine"}
                    tabIndex={shownStart === "racine" ? 0 : -1}
                    onClick={() => setStart("racine")}
                    title={t("Racine seule")}
                    detail={t("Vous ajoutez chaque unité, son échelon, sa fonction et son rôle.")}
                  />
                </div>
              </Motion>
            )
          ) : (
            <Motion origin="fade" className="space-y-4">
              <div
                className="grid gap-2"
                role="radiogroup"
                aria-label={t("Niveau de la formation")}
                onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => setKind(AMI_KINDS[i]!.key))}
              >
                <span className="block text-ui font-medium text-ink-soft">{t("Nature")}</span>
                {AMI_KINDS.map((option, i) => (
                  <ChoiceCard
                    key={option.key}
                    radio
                    selected={kind === option.key}
                    tabIndex={rovingTabIndex(i, AMI_KINDS.findIndex((o) => o.key === kind), AMI_KINDS.map(() => true))}
                    onClick={() => {
                      setKind(option.key);
                      setCopyCode("");
                    }}
                    icon={<UnitSymbol sidc={kindSidc(option.key)} size={10} box={30} />}
                    title={t(option.title)}
                    detail={t(option.detail)}
                  />
                ))}
              </div>
              {identity}
              <Segmented
                label={t("Organisation")}
                options={[
                  { value: "gtia", label: t("Groupements tactiques (GTIA, S/GTIA)") },
                  { value: "organique", label: t("Unités organiques (bataillons, compagnies)") },
                ]}
                value={organisation}
                onChange={setOrganisation}
              />
              <Field label={t("Nation")}>
                <Select className={inputClass} value={nation} placeholder={t("Facultative")} aria-label={t("Nation")} onChange={(e) => setNation(e.target.value)}>
                  <option value="">{t("— Non précisée —")}</option>
                  {nations().map((n) => (
                    <option key={n.code} value={n.code}>
                      {n.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="grid gap-2" role="radiogroup" aria-label={t("Départ")}>
                <span className="block text-ui font-medium text-ink-soft">{t("Départ")}</span>
                <ChoiceCard
                  radio
                  selected={start === "structure"}
                  tabIndex={start === "structure" ? 0 : -1}
                  onClick={() => setStart("structure")}
                  title={t("Structure type")}
                  detail={t("Ce que la manœuvre de son niveau place, avec sa fonction ; vous l'adaptez ensuite.")}
                />
                <ChoiceCard
                  radio
                  selected={start === "racine"}
                  tabIndex={start === "racine" ? 0 : -1}
                  onClick={() => setStart("racine")}
                  title={t("Racine seule")}
                  detail={t("Vous ajoutez chaque unité, son échelon et sa fonction.")}
                />
                <ChoiceCard
                  radio
                  selected={start === "copie"}
                  tabIndex={start === "copie" ? 0 : -1}
                  onClick={() => setStart("copie")}
                  disabled={noCopy}
                  title={t("Copie d'un ordre de bataille fourni")}
                  detail={
                    noCopy
                      ? t("Aucun ordre de bataille fourni avec Generex n'a cette nature.")
                      : kind === "gtia"
                        ? t("Un GTIA d'une brigade fournie avec Generex (armée de Terre française), à adapter.")
                        : t("Une formation fournie avec Generex (armée de Terre française), à adapter.")
                  }
                />
                <Presence show={start === "copie" && !noCopy}>
                  {start === "copie" && !noCopy && (
                    <Motion origin="fade" ref={copyRef} className="rounded-lg">
                      <Select className={inputClass} value={copyCode} placeholder={t("Ordre de bataille à copier")} aria-label={t("Ordre de bataille à copier")} onChange={(e) => setCopyCode(e.target.value)}>
                        {copyGroups.map((group) =>
                          group ? (
                            <optgroup key={group} label={t("Brigade {group}", { group })}>
                              {copyChoices
                                .filter((c) => c.group === group)
                                .map((c) => (
                                  <option key={c.value} value={c.value}>
                                    {c.label}
                                  </option>
                                ))}
                            </optgroup>
                          ) : (
                            copyChoices
                              .filter((c) => c.group === null)
                              .map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))
                          ),
                        )}
                      </Select>
                    </Motion>
                  )}
                </Presence>
              </div>
            </Motion>
          )}
          <Presence show={preview !== null}>
            {preview && (
              <Motion origin="fade" className="space-y-1.5">
                <span className="block text-ui font-medium text-ink-soft">{t("Aperçu")}</span>
                <div className="rounded-lg border border-line bg-canvas px-3 py-2">
                  {/* Racine en grand, numéros dessinés comme sur la carte ; subordonnés en lignes nommées, numéros
                      « T ⁄ M » en texte (illisibles dessinés à cette taille), comme dans l'arbre de l'éditeur ; rôle
                      dans la manœuvre côté ennemi. */}
                  <div className="flex items-center gap-2.5">
                    <UnitSymbol sidc={sidcOf({ side, app6: chosenFunction.app6, echelon: rootEchelon, tf: rootTf })} size={28} left={drawableNumber(number)} right={drawableNumber(higher)} box={96} />
                    <span className="min-w-0 truncate text-ui font-medium text-ink">{preview.name}</span>
                  </div>
                  {preview.children.length > 0 && (
                    <ul className="mt-1.5 border-t border-line pl-4">
                      {preview.children.map((child, i) => (
                        <li key={i} className="flex min-h-8 items-center gap-2 border-b border-line py-1.5 last:border-b-0">
                          <UnitSymbol
                            sidc={sidcOf({ side, app6: child.app6, echelon: child.echelon, tf: defaultTf(ctx, organisation, child.echelon ?? "15") })}
                            size={11}
                            box={32}
                          />
                          <span className="w-14 shrink-0 font-mono text-meta text-ink-soft tabular-nums">
                            {child.number || "–"}
                            {cleanNumber(number) ? <span className="text-ink-muted"> ⁄ {cleanNumber(number)}</span> : null}
                          </span>
                          {/* Nom en tête, rôle et subordonnés dessous : rien ne l'écrase à largeur de téléphone. */}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-ui text-ink">{child.name}</span>
                            {((enemy && child.role) || child.children.length > 0) && (
                              <span className="block truncate text-meta text-ink-muted">
                                {enemy && child.role ? <span className="text-ink-soft">{eniRoleLabel(child.role)}</span> : null}
                                {enemy && child.role && child.children.length > 0 ? " · " : null}
                                {child.children.length > 0 ? plural(child.children.length, t("subordonné"), t("subordonnés")) : null}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Motion>
            )}
          </Presence>
          <Presence show={create.isError}>{create.isError && <Callout tone="danger">{tServer(create.error.message)}</Callout>}</Presence>
        </DialogBody>
      </Dialog>
      {/* Hors de la fenêtre : son zoom d'apparition (transform) confinerait la palette à sa largeur. */}
      <Presence show={choosing}>
        {choosing && (
          <App6Palette
            palette={palette}
            unit={{
              side,
              echelon: rootEchelon,
              hq: false,
              tf: rootTf,
              number: drawableNumber(number),
              higher: drawableNumber(higher),
              fonction: chosenFunction.key,
              app6: chosenFunction.app6,
            }}
            onClose={() => setChoosing(false)}
            onChoose={(entry, app6) => {
              setFonction({ key: entry.key, app6 });
              setChoosing(false);
            }}
          />
        )}
      </Presence>
    </>
  );
}
